// Adapter contract + structural detection sample (Phase 1a).
//
// Lockstep with evals/adapters/_contract.py — see that module's docstring for
// the full rationale. The format-read redesign (spec §3–§4) decouples
// *detection* from *parsing*. Every adapter implements:
//
//   identify(sample) -> score   // float 0..1; 0 == "not me".
//                               // cheap + structural, sampled across the WHOLE
//                               // document — NOT page-1 anchored.
//   read(input, opts)  -> ...   // the existing ingest entry point, unchanged.
//
// A Sample is the structural evidence the detector looks at, built once per
// file (a stratified slice of extracted page text for PDFs, the header row for
// CSVs) and handed to every adapter's identify(). The scoring must classify
// consistently with the Python engine; because pdf.js and pdfplumber diverge on
// intra-token spacing, compare normalized where exact text matters
// (normalizeWs) — structural token counts are robust to it.

// How many pages to sample for a PDF fingerprint. Stratified across the whole
// document (head, interior, tail) so a cover-less export still scores.
// Lockstep with _contract.py MAX_SAMPLE_PAGES.
export const MAX_SAMPLE_PAGES = 12;

/** Collapse whitespace runs to single spaces and trim. Lockstep normalize_ws. */
export function normalizeWs(s: string): string {
  return s.replace(/\s+/g, " ").trim();
}

export type SampleKind = "pdf" | "csv" | "unknown";

export interface Sample {
  kind: SampleKind;
  /** Stratified slice of extracted page texts (PDF). */
  pages: string[];
  /** Raw text chunk (CSV) or empty for PDFs. */
  text: string;
  /** Parsed CSV header row (CSV only). */
  csvHeaders: string[];
  pageCount: number;
  isTruncated: boolean;
  filename: string;
}

/** All sampled page texts joined (PDF) or the raw text (CSV). */
export function combinedText(sample: Sample): string {
  if (sample.pages.length > 0) return sample.pages.join("\n");
  return sample.text;
}

// Pick up to k page indices spread across [0, n). Always includes first + last;
// the rest are evenly spaced. Lockstep with _contract.py _stratified_indices.
export function stratifiedIndices(n: number, k: number): number[] {
  if (n <= 0) return [];
  if (n <= k) return Array.from({ length: n }, (_, i) => i);
  const set = new Set<number>();
  for (let i = 0; i < k; i++) {
    set.add(Math.round((i * (n - 1)) / (k - 1)));
  }
  return [...set].sort((a, b) => a - b);
}

/**
 * Build a Sample from already-extracted page texts. When `pages` is the full
 * list and longer than MAX_SAMPLE_PAGES, a stratified slice is taken so
 * detection stays cheap on huge exports. Lockstep with build_pdf_sample.
 */
export function buildPdfSample(
  pages: string[],
  opts: { totalPages?: number; filename?: string } = {},
): Sample {
  const n = pages.length;
  const total = opts.totalPages ?? n;
  let sampled: string[];
  let truncated: boolean;
  if (n > MAX_SAMPLE_PAGES) {
    const idxs = stratifiedIndices(n, MAX_SAMPLE_PAGES);
    sampled = idxs.map((i) => pages[i]);
    truncated = true;
  } else {
    sampled = [...pages];
    truncated = total > n;
  }
  return {
    kind: "pdf",
    pages: sampled,
    text: "",
    csvHeaders: [],
    pageCount: total,
    isTruncated: truncated,
    filename: opts.filename ?? "",
  };
}

/** Build a Sample from a CSV's parsed header row (+ optional raw text). */
export function buildCsvSample(
  header: string[],
  opts: { text?: string; filename?: string } = {},
): Sample {
  return {
    kind: "csv",
    pages: [],
    text: opts.text ?? "",
    csvHeaders: header.map((h) => h.trim()),
    pageCount: 0,
    isTruncated: false,
    filename: opts.filename ?? "",
  };
}

/**
 * The uniform adapter contract (the Tika/beangulp spine).
 *
 * `identify` is cheap + structural and must never throw — a parse failure is
 * not "wrong format". `read` is the existing ingest entry point, unchanged.
 */
export interface Adapter<ReadArgs extends unknown[] = unknown[], ReadResult = unknown> {
  /** Stable format key: "ofw" | "civil-communicator" | "generic-csv". */
  readonly name: string;
  /**
   * Score at/above which the registry auto-routes with no confirm step
   * (per-format; spec §4). Distinctive formats get a high bar.
   */
  readonly autoThreshold: number;
  /** Structural confidence in [0, 1] that `sample` is this format. */
  identify(sample: Sample): number;
  /** Deterministic parse → canonical artifacts (the existing entry point). */
  read(...args: ReadArgs): ReadResult;
}
